import { Box, Button, CircularProgress, IconButton, InputAdornment, MenuItem, Popover, Stack, Switch, Typography } from "@mui/material";
import { PinnacleTextField } from "../../../components/PinnacleTextField";
import { E_FIELD_REQUIRED, HOW_TO_GET_VIMEOID_TUTORIAL_GIF } from "../../../utils/constants";
import { Program } from "../../../api/interfaces/Program";
import { FormData } from "../hooks/useVideosForm";
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useCallback, useEffect, useState } from "react";
import { Subject } from "../../../api/interfaces/Subject";
import { getSubjects } from "../../../api/services/subjects";
import { handleApiError } from "../../../utils/errorHandler";
import { pushNotification } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { extractVimeoId } from "../../../utils/extractVimeoId";
import { useDebounce } from "use-debounce";
import { getVimeoVideoUrlInfo } from "../../../api/services/video";
import { formatDuration } from "../../../utils/formatDuration";

interface EditVideoDrawerContentProps {
    formData: FormData;
    touchedFields: Record<keyof FormData, boolean>;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur: (field: keyof FormData) => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    programs: Program[];
    isFormValid: () => boolean;
    handleUploadClick: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedImage: File | null;
    isFormModified: boolean;
}

const EditVideoDrawerContent: React.FC<EditVideoDrawerContentProps> = ({
    formData,
    touchedFields,
    onChange,
    onBlur,
    onSubmit,
    isSubmitting,
    programs,
    isFormValid,
    handleUploadClick,
    fileInputRef,
    handleFileChange,
    selectedImage,
    isFormModified
}) => {
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [isSubjectsLoading, setIsSubjectsLoading] = useState<boolean>(false);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [vimeoVideoUrl2, setVimeoVideoUrl2] = useState<string>(
        extractVimeoId(formData.videoUrl2) || ''
    );
    const [isVimeoFetching, setIsVimeoFetching] = useState<boolean>(false);
    const [isVimeoAutoAddedDuration, setIsVimeoAutoAddedDuration] = useState<boolean>(false);

    const [debouncedVideoUrl] = useDebounce(vimeoVideoUrl2, 500);

    const handleOpenVimeoTutorial = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const fetchSubjects = useCallback(async (programId: string) => {
        try {
            const subjectsResponse = await getSubjects({ programId });
            setSubjects(subjectsResponse.data.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const handleProgramChange = async (e: any) => {
        try {
            setIsSubjectsLoading(true);
            const programId = e.target.value;
            await fetchSubjects(programId);
            const syntheticEvent = {
                target: {
                    name: 'programId',
                    value: programId,
                }
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubjectsLoading(false);
        } finally {
            setIsSubjectsLoading(false);
        }
    };

    const handleVimeoChange = (e: any) => {
        setVimeoVideoUrl2(e.target.value);
    }

    // useEffect(() => {
        // if (!debouncedVideoUrl && vimeoVideoUrl2) {
        //     setIsVimeoAutoAddedDuration(false);

        //     const clearDurationEvent = {
        //         target: {
        //             name: 'duration',
        //             value: '',
        //         },
        //     } as React.ChangeEvent<HTMLInputElement>;

        //     onChange(clearDurationEvent);
        //     return;
        // }

    //     const fetchVideoInfo = async () => {
    //         try {
    //             if (vimeoVideoUrl2) {
    //                 setIsVimeoFetching(true);

    //                 const response = await getVimeoVideoUrlInfo(debouncedVideoUrl);
    //                 const duration = formatDuration(response.duration);

    //                 const videoUrlEvent = {
    //                     target: {
    //                         name: 'videoUrl2',
    //                         value: debouncedVideoUrl,
    //                     },
    //                 } as React.ChangeEvent<HTMLInputElement>;

    //                 const durationEvent = {
    //                     target: {
    //                         name: 'duration',
    //                         value: duration,
    //                     },
    //                 } as React.ChangeEvent<HTMLInputElement>;

    //                 onChange(videoUrlEvent);
    //                 setIsVimeoAutoAddedDuration(true);
    //                 onChange(durationEvent);
    //             }
    //         } catch (error) {
    //             setIsVimeoAutoAddedDuration(false);

    //             const clearDurationEvent = {
    //                 target: {
    //                     name: 'duration',
    //                     value: '',
    //                 },
    //             } as React.ChangeEvent<HTMLInputElement>;

    //             onChange(clearDurationEvent);

    //             handleApiError(error, {
    //                 notify: () => {
    //                     pushNotification({
    //                         details: "Failed to fetch Vimeo video info. Please ensure the video ID is correct and the video has finished uploading.",
    //                         type: NotificationTypes.Error,
    //                     });
    //                 },
    //             });
    //         } finally {
    //             setIsVimeoFetching(false);
    //         }
    //     };

    //     fetchVideoInfo();
    // }, [debouncedVideoUrl, onChange]);

    useEffect(() => {
        const init = async () => {
            try {
                if (formData.programId) {
                    setIsSubjectsLoading(true);
                    await fetchSubjects(formData.programId);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsSubjectsLoading(false);
            } finally {
                setIsSubjectsLoading(false);
            }
        }
        init();
    }, [])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PinnacleTextField
                select
                label="Program"
                name="programId"
                value={formData.programId}
                onChange={(e) => handleProgramChange(e)}
                fullWidth
                onBlur={() => onBlur('programId')}

                error={touchedFields.programId && !formData.programId.trim()}
                helperText={
                    touchedFields.programId && !formData.programId.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            >
                {programs.map((program: Program) => (
                    <MenuItem key={program._id} value={program._id}>
                        <Typography
                            noWrap
                            sx={{
                                maxWidth: '500px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                            }}
                        >
                            {program.name}
                        </Typography>
                    </MenuItem>
                ))}
            </PinnacleTextField>

            <PinnacleTextField
                select
                label="Subject"
                name="subjectId"
                value={formData.subjectId}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('subjectId')}
                error={touchedFields.subjectId && !formData.subjectId.trim()}
                helperText={
                    touchedFields.subjectId && !formData.subjectId.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                slotProps={{
                    input: {
                        endAdornment: (
                            <>
                                {isSubjectsLoading ? <CircularProgress size={20} /> : null}
                            </>
                        ),
                    }
                }}
                disabled={isSubmitting || isSubjectsLoading}
            >
                {subjects.map((subject: Subject) => (
                    <MenuItem key={subject._id} value={subject._id}>
                        <Typography
                            noWrap
                            sx={{
                                maxWidth: '500px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                            }}
                        >
                            {subject.name}
                        </Typography>
                    </MenuItem>
                ))}
            </PinnacleTextField>

            <PinnacleTextField
                label="Title"
                name="title"
                value={formData.title}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('title')}
                error={touchedFields.title && !formData.title.trim()}
                helperText={
                    touchedFields.title && !formData.title.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={onChange}
                multiline
                rows={5}
                fullWidth
                onBlur={() => onBlur('description')}
                error={touchedFields.description && !formData.description.trim()}
                helperText={
                    touchedFields.description && !formData.description.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <Stack spacing={2} sx={{ mb: 2 }}>
                <Box
                    onClick={handleUploadClick}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        {selectedImage ? (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                        noWrap
                                        sx={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        {selectedImage.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click to change cover
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Upload Video Thumbnail
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Browse file to upload
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>

                    <input
                        type="file"
                        accept="image/jpeg, image/jpg, image/png, image/svg+xml"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Box
                        sx={{
                            border: '2px solid #ccc',
                            borderRadius: '8px',
                            p: 1,
                            textAlign: 'center',
                            '&:hover': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        <Typography variant="body1" sx={{ color: '#171717', mb: 1 }}>
                            {selectedImage ? "Selected Thumnail" : "Current Thumbnail"}
                        </Typography>
                        {selectedImage ? (
                            <Box
                                component="img"
                                src={URL.createObjectURL(selectedImage)}
                                alt="Selected Cover"
                                sx={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    borderRadius: 2,
                                    alignSelf: 'center',
                                }}
                            />
                        ) : formData.thumbnail ? (
                            <Box
                                component="img"
                                src={formData.thumbnail}
                                alt={formData.title}
                                sx={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    borderRadius: 2,
                                    alignSelf: 'center',
                                }}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Stack>

            <PinnacleTextField
                label="Video Player (Rumble URL)"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('videoUrl')}
                error={touchedFields.videoUrl && !formData.videoUrl.trim()}
                helperText={
                    touchedFields.videoUrl && !formData.videoUrl.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Video Player (Odysee URL)"
                name="videoUrl1"
                value={formData.videoUrl1}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('videoUrl1')}
                // error={touchedFields.videoUrl1 && !formData.videoUrl1.trim()}
                // helperText={
                //     touchedFields.videoUrl1 && !formData.videoUrl1.trim()
                //         ? E_FIELD_REQUIRED
                //         : ' '
                // }
                disabled={isSubmitting}
            />

            <Box display="flex" alignItems="flex-start" gap={1} position="relative">
                <Box flex={1}>
                    <PinnacleTextField
                        label="Video Player (Vimeo Video ID)"
                        name="videoUrl2"
                        value={vimeoVideoUrl2}
                        onChange={handleVimeoChange}
                        fullWidth
                        onBlur={() => onBlur('videoUrl2')}
                        error={touchedFields.videoUrl2 && !formData.videoUrl2.trim()}
                        // helperText={
                        //     touchedFields.videoUrl2 && !formData.videoUrl2.trim()
                        //         ? E_FIELD_REQUIRED
                        //         : ' '
                        // }
                        disabled={isSubmitting}
                        slotProps={{
                            input: {
                                endAdornment: isVimeoFetching ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : null,
                            },
                        }}
                    />
                </Box>

                <IconButton size="medium" onClick={handleOpenVimeoTutorial}>
                    <HelpOutlineIcon fontSize="medium" />
                </IconButton>

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                p: 2,
                                maxWidth: 550,
                                borderRadius: 2
                            }
                        }
                    }}
                >
                    <Typography variant="subtitle2" gutterBottom>
                        How to get Vimeo Video ID
                    </Typography>
                    <a
                        href={HOW_TO_GET_VIMEOID_TUTORIAL_GIF}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Box
                            component="img"
                            src={HOW_TO_GET_VIMEOID_TUTORIAL_GIF}
                            alt="How to Copy Vimeo ID Tutorial GIF"
                            sx={{ width: "100%", borderRadius: 1 }}
                        />
                    </a>
                </Popover>
            </Box>


            <PinnacleTextField
                label="Duration"
                name="duration"
                value={formData.duration}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('duration')}
                error={touchedFields.duration && !formData.duration.trim()}
                helperText={
                    touchedFields.duration && !formData.duration.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                // disabled={isSubmitting || isVimeoAutoAddedDuration}
                disabled={isSubmitting}
            />

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", mb: 3 }}
            >
                <Typography variant="body2" sx={{ color: '#818181' }}>
                    Set this video in <Box component="span" sx={{ fontWeight: 'bold' }}>“TRIAL”</Box> mode?
                </Typography>

                <Switch
                    checked={formData.isTrial}
                    onChange={onChange}
                    disabled={isSubmitting}
                    name="isTrial"
                />
            </Box>

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", mb: 3 }}
            >
                <Typography variant="body2" sx={{ color: '#818181' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Show</Box> this video in the list? Toggle to include or hide.
                </Typography>

                <Switch
                    checked={formData.isVisible}
                    onChange={onChange}
                    disabled={isSubmitting}
                    name="isVisible"
                />
            </Box>

            <Button
                variant="contained"
                color="primary"
                onClick={onSubmit}
                // disabled={isSubmitting || !isFormValid() || !isFormModified}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
                UPDATE
            </Button>
        </Box>
    );
};

export default EditVideoDrawerContent;